import { useCallback, useRef } from 'react';
import { useCompanyMutations } from './use-company-mutations';
import { AccountPropertiesInput } from '@/lib/graphql/types-and-hooks';
import { isLovablePreview } from '@/lib/utils/environment';

interface PendingUpdate {
  accountId: string;
  properties: Partial<AccountPropertiesInput>;
  customProperties?: Record<string, any>;
  timeoutId: NodeJS.Timeout;
}

// Map UI column keys to backend property names. Exported for the field-history
// lookup (src/lib/field-history/field-key.ts), which must ask for the exact
// property this hook writes.
export const COLUMN_TO_PROPERTY_MAP: Record<string, string> = {
  name: 'name',
  website: 'domain', // ⚠️ Primary domain
  phone: 'phone',
  industry: 'industry',
  employees: 'companySizeCategory', // ⚠️ NOT numberOfEmployees
  revenue: 'revenueRange', // ⚠️ NOT annualRevenue
  description: 'description',
  linkedin: 'linkedinUrl',
  foundingYear: 'foundedYear',
  city: 'city',
  stateProvince: 'state',
  country: 'country',
  continent: 'continent',
  postalCode: 'postalCode',
  companyType: 'entityType',
  funding: 'raisedAmount',
  technologies: 'techTags',
};

export const useDebouncedCompanyUpdate = (debounceMs: number = 500) => {
  const { updateAccount } = useCompanyMutations();
  const pendingUpdatesRef = useRef<Map<string, PendingUpdate>>(new Map());

  const scheduleUpdate = useCallback(
    (accountId: string, columnKey: string, value: string) => {
      if (isLovablePreview()) {
        return; // Skip updates in preview mode
      }

      // Cancel existing timeout for this account
      const existing = pendingUpdatesRef.current.get(accountId);
      if (existing?.timeoutId) {
        clearTimeout(existing.timeoutId);
      }

      // Build properties object
      let properties: Partial<AccountPropertiesInput> = existing?.properties || {};
      let customProperties: Record<string, any> = existing?.customProperties || {};

      // Check if this is a custom field (starts with custom_)
      if (columnKey.startsWith('custom_')) {
        // Extract the field name (remove custom_ prefix)
        const fieldName = columnKey.replace('custom_', '');

        // For numeric/currency fields, convert string to number if it's a valid number
        // The backend stores these with value_numeric metadata
        // For checkbox fields, empty string means unchecked (don't convert to number)
        let fieldValue: any = value;
        if (value && value !== "" && !isNaN(Number(value))) {
          // Check if this looks like a numeric value (no letters, only digits, dots, minus)
          if (/^-?\d*\.?\d+$/.test(value.trim())) {
            fieldValue = Number(value);
          }
        }

        customProperties = { ...customProperties, [fieldName]: fieldValue };
      } else if (columnKey === 'location') {
        // Special case: location → parse to city/state/country
        const parts = value.split(',').map(s => s.trim());
        properties = {
          ...properties,
          city: parts[0] || undefined,
          state: parts[1] || undefined,
          country: parts[2] || undefined,
        };
      } else if (columnKey === 'technologies') {
        const tags = value.split(',').map(s => s.trim()).filter(Boolean);
        properties = { ...properties, techTags: tags };
      } else if (columnKey === 'foundingYear') {
        const year = parseInt(value, 10);
        if (!isNaN(year)) {
          properties = { ...properties, foundedYear: year };
        }
      } else {
        // Standard property mapping
        const propertyKey = COLUMN_TO_PROPERTY_MAP[columnKey];
        if (propertyKey) {
          properties = { ...properties, [propertyKey]: value };
        }
      }

      // Schedule the update
      const timeoutId = setTimeout(async () => {
        const updatePayload: any = {
          id: accountId,
          properties: properties, // Always send properties (even if empty) - matches coffee-ui pattern
        };
        if (Object.keys(customProperties).length > 0) {
          updatePayload.customProperties = customProperties;
        }
        await updateAccount(updatePayload);
        pendingUpdatesRef.current.delete(accountId);
      }, debounceMs);

      pendingUpdatesRef.current.set(accountId, { accountId, properties, customProperties, timeoutId });
    },
    [updateAccount, debounceMs]
  );

  return { scheduleUpdate };
};
